import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Select, Table, Tooltip, message } from 'antd';
import { ExportOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { PAGE_QUERY_DSP_EXPENSE_REPORT } from '../../../graphql/queries/settlement/dspExpenseReportQueries';
import { EXPORT_DSP_EXPENSE_REPORT } from '../../../graphql/mutations/settlement/dspExpenseReportMutations';
import type {
    DspExpenseReportFilterInput,
    DspExpenseReportRow,
    ExportDspExpenseReportResponse,
    PageQueryDspExpenseReportResponse,
} from '../../../graphql/types/settlement/dspExpenseReport';
import { DspSelectFilter } from '../../../components/filters/DspSelectFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from './components/ParcelsDownloadModal';
import { BILL_TIMEZONE_OPTIONS, PACIFIC_TIMEZONE } from '../../../utils/timezoneFormatter';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';

/** 空值统一渲染为 `-`；不可用 0 兜底，0 会被读成「值为零」而非「无法计算」 */
const dash = (value: string | null | undefined) => (value == null || value === '' ? '-' : value);

/** 树形行：汇总行为父节点，同组明细行挂在其 children 下 */
type TreeRow = DspExpenseReportRow & { children?: DspExpenseReportRow[] };

/**
 * 后端返回的扁平行（每组「汇总行 + N 条明细行」）转成 antd 的树形数据。
 *
 * 分页单位是「组」，组不会跨页，故一页内每条明细必能找到它的汇总行父节点。
 */
const toTreeRows = (rows: DspExpenseReportRow[]): TreeRow[] => {
    const result: TreeRow[] = [];
    const summaryByGroup = new Map<string, TreeRow>();
    rows.forEach((row) => {
        if (row.rowType === 'SUMMARY') {
            const node: TreeRow = { ...row, children: [] };
            summaryByGroup.set(row.groupKey, node);
            result.push(node);
            return;
        }
        const parent = summaryByGroup.get(row.groupKey);
        // 汇总行理应先到；万一缺失也要把明细行平铺出来，绝不能静默丢弃（金额会凭空变少）
        if (parent) {
            parent.children!.push(row);
        } else {
            result.push({ ...row });
        }
    });
    // children 为空数组时 antd 依然会画展开箭头，点开一片空白，故删除该字段
    result.forEach((node) => {
        if (node.children && node.children.length === 0) {
            delete node.children;
        }
    });
    return result;
};

/** 组维度的列（账期 / DSP）：只画在汇总行，明细行靠树形缩进体现归属 */
const summaryOnly = (value: string, record: DspExpenseReportRow) =>
    (record.rowType === 'SUMMARY' ? value : null);

const DspExpenseStatisticsPage: React.FC = () => {
    const { t } = useTranslation();

    const [dspIds, setDspIds] = useState<string[] | undefined>(undefined);
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    // 默认美西：账期日期需要一个时区来确定「这一天」的边界，与后端默认值保持一致
    const [timeZoneId, setTimeZoneId] = useState<string>(PACIFIC_TIMEZONE);
    // 首屏就带上默认时区，避免「首屏」与「点过查询之后」两次的日期口径不一致
    const [appliedFilters, setAppliedFilters] = useState<DspExpenseReportFilterInput>({
        timeZoneId: PACIFIC_TIMEZONE,
    });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const { data, loading } = useQuery<PageQueryDspExpenseReportResponse>(PAGE_QUERY_DSP_EXPENSE_REPORT, {
        variables: { filters: appliedFilters, page, pageSize },
        fetchPolicy: 'cache-and-network',
    });

    // cutover 打到 NEW 且转发失败时 success=false（list 恒为空，不当异常抛，本身是正常响应），
    // 用 useEffect 而不是渲染期直接调用，避免每次渲染都重复弹一次 toast
    useEffect(() => {
        const result = data?.pageQueryDspExpenseReport;
        if (result && !result.success) {
            message.error(result.message || t('settlement.dspExpenseStatistics.queryFailed'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const [exportDspExpenseReport, { loading: exporting }] =
        useMutation<ExportDspExpenseReportResponse>(EXPORT_DSP_EXPENSE_REPORT);

    const buildFilters = useCallback((): DspExpenseReportFilterInput => ({
        dspIds: dspIds && dspIds.length > 0 ? dspIds.map(Number) : undefined,
        periodStartFrom: range?.[0] ? range[0].format(DATE_FORMAT) : undefined,
        periodEndTo: range?.[1] ? range[1].format(DATE_FORMAT) : undefined,
        timeZoneId,
    }), [dspIds, range, timeZoneId]);

    const handleSearch = () => {
        setAppliedFilters(buildFilters());
        setPage(1);
    };

    const handleReset = () => {
        setDspIds(undefined);
        setRange(null);
        setTimeZoneId(PACIFIC_TIMEZONE);
        setAppliedFilters({ timeZoneId: PACIFIC_TIMEZONE });
        setPage(1);
    };

    const handleExport = async () => {
        try {
            // 导出用「已查询」的条件而非输入框当前值，避免改了条件未点查询就导出，
            // 导致 Excel 与页面所见不一致
            const result = await exportDspExpenseReport({ variables: { filters: appliedFilters } });
            const exportResult = result.data?.exportDspExpenseReport;
            const fileName = exportResult?.success ? exportResult.data : null;
            if (!fileName) {
                message.error(exportResult?.message || t('settlement.dspExpenseStatistics.exportFailed'));
                return;
            }
            setGeneratedFileName(fileName);
            setDownloadModalVisible(true);
        } catch (error) {
            message.error(`${t('settlement.dspExpenseStatistics.exportFailed')}: ${(error as Error).message}`);
        }
    };

    /** 带说明图标的列头，用于取数口径与已知不准的列 */
    const headerWithHint = (label: string, hint: string) => (
        <span className="inline-flex items-center gap-1">
            {label}
            <Tooltip title={hint}>
                <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
        </span>
    );

    const columns = useMemo<ColumnsType<DspExpenseReportRow>>(() => {
        const p = 'settlement.dspExpenseStatistics.columns';
        return [
            {
                title: t(`${p}.period`),
                dataIndex: 'periodLabel',
                width: 210,
                fixed: 'left',
                render: summaryOnly,
            },
            {
                title: t(`${p}.dspFullName`),
                dataIndex: 'dspFullName',
                width: 200,
                fixed: 'left',
                render: summaryOnly,
            },
            {
                title: t(`${p}.dsp`),
                dataIndex: 'dspName',
                width: 150,
                fixed: 'left',
                render: summaryOnly,
            },
            { title: t(`${p}.oneSortingCode`), dataIndex: 'oneSortingCode', width: 100, render: dash },
            { title: t(`${p}.twoSortingCode`), dataIndex: 'twoSortingCode', width: 100, render: dash },
            { title: t(`${p}.threeSortingCode`), dataIndex: 'threeSortingCode', width: 100, render: dash },
            {
                title: headerWithHint(t(`${p}.unitPrice`), t('settlement.dspExpenseStatistics.hints.unitPrice')),
                dataIndex: 'unitPrice',
                width: 130,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.driverCnt`), t('settlement.dspExpenseStatistics.hints.driverCnt')),
                dataIndex: 'driverCnt',
                width: 140,
                align: 'right',
            },
            { title: t(`${p}.deliveredCnt`), dataIndex: 'deliveredCnt', width: 110, align: 'right' },
            { title: t(`${p}.deductionOnOrder`), dataIndex: 'deductionOnOrder', width: 140, align: 'right' },
            {
                title: headerWithHint(
                    t(`${p}.deductionTotal`),
                    t('settlement.dspExpenseStatistics.hints.deductionTotal'),
                ),
                dataIndex: 'deductionTotal',
                width: 150,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(
                    t(`${p}.dspOtherAdjustment`),
                    t('settlement.dspExpenseStatistics.hints.dspOtherAdjustment'),
                ),
                dataIndex: 'dspOtherAdjustment',
                width: 160,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.depositAmount`), t('settlement.dspExpenseStatistics.hints.depositAmount')),
                dataIndex: 'depositAmount',
                width: 150,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.insuranceAmount`), t('settlement.dspExpenseStatistics.hints.insuranceAmount')),
                dataIndex: 'insuranceAmount',
                width: 150,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(
                    t(`${p}.sameAddressReduction`),
                    t('settlement.dspExpenseStatistics.hints.sameAddressReduction'),
                ),
                dataIndex: 'sameAddressReduction',
                width: 160,
                align: 'right',
            },
            {
                title: headerWithHint(
                    t(`${p}.settlementAmountExclNonOrder`),
                    t('settlement.dspExpenseStatistics.hints.settlementAmountExclNonOrder'),
                ),
                dataIndex: 'settlementAmountExclNonOrder',
                width: 200,
                align: 'right',
            },
            {
                title: headerWithHint(
                    t(`${p}.settlementAmountInclNonOrder`),
                    t('settlement.dspExpenseStatistics.hints.settlementAmountInclNonOrder'),
                ),
                dataIndex: 'settlementAmountInclNonOrder',
                width: 220,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.settlementAmountInclDepositInsurance`), t('settlement.dspExpenseStatistics.hints.settlementAmountInclDepositInsurance')),
                dataIndex: 'settlementAmountInclDepositInsurance',
                width: 230,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.costAmountBill`), t('settlement.dspExpenseStatistics.hints.costAmountBill')),
                dataIndex: 'costAmountBill',
                width: 190,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.invoiceBillDiff`), t('settlement.dspExpenseStatistics.hints.invoiceBillDiff')),
                dataIndex: 'invoiceBillDiff',
                width: 200,
                align: 'right',
                render: dash,
            },
            { title: t(`${p}.fullPriceDeliveredCnt`), dataIndex: 'fullPriceDeliveredCnt', width: 130, align: 'right' },
            { title: t(`${p}.discountDeliveredCnt`), dataIndex: 'discountDeliveredCnt', width: 130, align: 'right' },
            {
                title: headerWithHint(
                    t(`${p}.parcelPerDriver`),
                    t('settlement.dspExpenseStatistics.hints.parcelPerDriver'),
                ),
                dataIndex: 'parcelPerDriver',
                width: 150,
                align: 'right',
                render: dash,
            },
            {
                title: headerWithHint(t(`${p}.costPerParcel`), t('settlement.dspExpenseStatistics.hints.costPerParcel')),
                dataIndex: 'costPerParcel',
                width: 140,
                align: 'right',
                render: dash,
            },
            { title: t(`${p}.paymentStatus`), dataIndex: 'paymentStatus', width: 120, render: dash },
            { title: t(`${p}.invoiceAmount`), dataIndex: 'invoiceAmount', width: 150, align: 'right', render: dash },
            { title: t(`${p}.paymentMethod`), dataIndex: 'paymentMethod', width: 140, render: dash },
        ];
        // headerWithHint 是纯函数，不随渲染变化，故只依赖 t
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    // 必须 useMemo：`?? []` 每次渲染都会造出新数组，直接作为下游 useMemo 的依赖等于没缓存
    const rows = useMemo(
        () => data?.pageQueryDspExpenseReport?.list ?? [],
        [data],
    );
    const total = data?.pageQueryDspExpenseReport?.total ?? 0;

    const treeRows = useMemo(() => toTreeRows(rows), [rows]);
    const expandableKeys = useMemo(
        () => treeRows.filter((row) => row.children).map((row) => row.rowKey),
        [treeRows],
    );
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

    // 翻页或改筛选条件后行集已变，旧的展开态没有意义。
    // 依赖必须是「查询条件」而不能是 treeRows —— data 未就绪时 `?? []` 每次渲染都产生新数组，
    // 以它为依赖会 setState → 重渲染 → 依赖又变，陷入无限渲染。
    useEffect(() => {
        setExpandedKeys([]);
    }, [page, pageSize, appliedFilters]);

    const allExpanded = expandableKeys.length > 0 && expandedKeys.length === expandableKeys.length;

    return (
        <div className="space-y-4">
            <Card title={t('settlement.dspExpenseStatistics.filterTitle')}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <DspSelectFilter
                        label={t('settlement.dspExpenseStatistics.dsp')}
                        value={dspIds}
                        onChange={setDspIds}
                    />
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            {t('settlement.dspExpenseStatistics.billingPeriod')}
                        </label>
                        <RangePicker
                            className="w-full"
                            value={range}
                            onChange={(dates) => {
                                setRange(dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : null);
                            }}
                            format={DATE_FORMAT}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            {t('settlement.dspExpenseStatistics.timeZone')}
                        </label>
                        <Select
                            className="w-full"
                            value={timeZoneId}
                            onChange={setTimeZoneId}
                            options={BILL_TIMEZONE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} className="flex-1">
                            {t('common.search')}
                        </Button>
                        <Button onClick={handleReset} className="flex-1">
                            {t('common.reset')}
                        </Button>
                    </div>
                    <div className="flex items-end">
                        <Button
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                            loading={exporting}
                            disabled={!total}
                        >
                            {t('settlement.dspExpenseStatistics.export')}
                        </Button>
                    </div>
                </div>
            </Card>

            <Card title={t('settlement.dspExpenseStatistics.listTitle')}>
                <div className="mb-3 flex items-start justify-between gap-4">
                    <div className="text-sm text-gray-500">
                        {t('settlement.dspExpenseStatistics.structureHint')}
                    </div>
                    <Button
                        size="small"
                        disabled={!expandableKeys.length}
                        onClick={() => setExpandedKeys(allExpanded ? [] : expandableKeys)}
                        className="shrink-0"
                    >
                        {allExpanded
                            ? t('settlement.dspExpenseStatistics.collapseAll')
                            : t('settlement.dspExpenseStatistics.expandAll')}
                    </Button>
                </div>
                <Table<TreeRow>
                    columns={columns}
                    dataSource={treeRows}
                    rowKey="rowKey"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 4400 }}
                    size="small"
                    className="mb-4"
                    // 默认收起，先给财务一屏账单维度总览；需要看线路时再点开
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        expandRowByClick: true,
                        indentSize: 16,
                    }}
                    // 汇总行与明细行必须视觉可分：混在一起会被财务一并求和从而重复计算
                    rowClassName={(record) =>
                        record.rowType === 'SUMMARY' ? 'bg-blue-50 font-semibold cursor-pointer' : ''
                    }
                />

                <TablePaginationBar
                    total={total}
                    current={page}
                    pageSize={pageSize}
                    onChange={(newPage, newPageSize) => {
                        setPage(newPage);
                        setPageSize(newPageSize);
                    }}
                />
            </Card>

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default DspExpenseStatisticsPage;
